import { BorderGridContainer, GridBox, H2, SegmentationBar, TypeWrapper } from '@ahmdigital/ui';
import { isEmpty, toLower } from 'lodash/fp';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Customer } from '../../../../selectors/select-customer/types';
import { ProductListSection } from '../../../../types/__codegen__/sanity/graphql';
import constants from '../../../../ahm-constants';
import CustomerActions from '../../../../actions/customer';
import getFrequencyOptions from '../../../../utils/get-frequency-options';
import getProductSectionProps from './utils/get-product-section-props';
import getScaleOptions from '../../../../utils/get-scale-options';
import PricingDrawer from '../../../../components/pricing-drawer';
import ProductListingOverlayActions from '../../../../actions/product-listing-overlay';
import ProductsSection from '../../../../components/sections/products-section';
import selectCustomer from '../../../../selectors/select-customer';
import selectProducts from '../../../../selectors/select-products';
import transformProductListsToOptions from './utils/transform-product-lists-to-options';

const StyledSegmentationWrapper = styled.div`
  margin: auto;
  max-width: 66rem;
`;

const FindCoverSection = ({
  defaultScale,
  name,
  productLists,
}: Pick<ProductListSection, 'defaultScale' | 'name' | 'productLists'>) => {
  const dataByProductType = productLists
    ? transformProductListsToOptions(
        productLists.filter((productList): productList is NonNullable<typeof productList> => productList != null),
      )
    : {};
  const availableProductTypes = Object.keys(dataByProductType);

  const dispatch = useDispatch();
  const onChangeFrequency = useCallback(
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'frequency' implicitly has an 'any' type.
    (frequency) => {
      dispatch(CustomerActions.changeFrequency(frequency));
    },
    [dispatch],
  );
  const onChangeProductType = useCallback(
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'productType' implicitly has an 'any' type.
    (productType) => {
      dispatch(CustomerActions.changeProductType(productType));
    },
    [dispatch],
  );
  const onChangeScale = useCallback(
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'scale' implicitly has an 'any' type.
    (scale) => {
      dispatch(CustomerActions.changeScale(scale));
    },
    [dispatch],
  );
  const onChangeState = useCallback(
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'state' implicitly has an 'any' type.
    (state) => {
      dispatch(CustomerActions.changeState(state));
    },
    [dispatch],
  );

  useEffect(() => {
    if (defaultScale) {
      dispatch(CustomerActions.changeScale(defaultScale as Customer['scale']));
    }
  }, [defaultScale, dispatch]);

  const onEdit = useCallback(() => {
    dispatch(ProductListingOverlayActions.productListingOverlayShowing());
  }, [dispatch]);
  const onRead = useCallback(() => {
    dispatch(ProductListingOverlayActions.productListingOverlayHidden());
  }, [dispatch]);

  const customer = useSelector(selectCustomer);
  const products = useSelector(selectProducts);
  if (isEmpty(products)) {
    return null;
  }

  const { frequency, scale, state } = customer;
  // Defaulting productType to Combined if it has not yet been set in redux state.
  // Also, page does not support package products so use Combined instead.
  let productType = customer?.productType ?? constants.PRODUCT_TYPE.COMBINED;
  if (productType === constants.PRODUCT_TYPE.PACKAGE) {
    productType = constants.PRODUCT_TYPE.COMBINED;
  }

  const validProductTypes = constants.PRODUCT_TYPE_OPTIONS.filter((option) =>
    availableProductTypes.includes(option.value),
  );

  const productTypeAvailable = availableProductTypes.includes(productType);
  if (!productTypeAvailable) {
    productType = (availableProductTypes[0] as typeof productType) ?? '';
  }

  const segmentationBarProps = {
    changeFrequency: onChangeFrequency,
    changeProductType: onChangeProductType,
    changeScale: onChangeScale,
    changeState: onChangeState,
    frequency,
    frequencyOptions: getFrequencyOptions({ endText: 'ly', format: toLower }),
    onEdit,
    onRead,
    productType,
    productTypeOptions: validProductTypes,
    scale,
    scaleOptions: getScaleOptions({ endText: 'cover', format: toLower }),
    showProductType: true,
    state,
    stateOptions: constants.STATE_OPTIONS,
  };

  return (
    <BorderGridContainer hideBorderTop>
      <GridBox padding="1rem">
        <TypeWrapper variant={['center', 'marginTopSpacer2', 'marginBottomSpacer6']}>
          <H2>{name}</H2>
          <StyledSegmentationWrapper>
            <SegmentationBar {...segmentationBarProps} />
          </StyledSegmentationWrapper>
          <ProductsSection
            {...getProductSectionProps({ dataByProductType, productType, products, scale })}
            withDrawer={false}
            withHorizontalScroll
            withNeedsLink
            withVerticalSpacing={false}
          />
          <PricingDrawer isProductWithHospitalServices={productType !== PRODUCT_TYPE.EXTRAS} />
        </TypeWrapper>
      </GridBox>
    </BorderGridContainer>
  );
};

export default FindCoverSection;
